import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

interface StoredRoute {
  handle: DetachedRouteHandle | null;
  expiry: number; // Expiry time in milliseconds
}
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private storedRoutes = new Map<string, StoredRoute>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    let key = this.getKey(route);
    return !!route?.data?.reuseComponent && !!route.component;
  }

  store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle | null,
  ): void {
    let key = this.getKey(route);
    // Calculate expiry time from route data or use default of 15 minutes
    const maxAgeMinutes = route?.data?.maxAge || 15;
    const expiry = Date.now() + maxAgeMinutes * 60 * 1000; // Convert minutes to milliseconds and add to current time

    // Evict older cache entries for the same component type
    const componentName = route?.component['name'] || '';
    Array.from(this.storedRoutes.keys()).forEach((existingKey) => {
      if (existingKey.startsWith(`${componentName}?`)) {
        this.storedRoutes.delete(existingKey);
      }
    });

    // Store the new route handle with its expiry time
    this.storedRoutes.set(key, { handle, expiry });
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    let key = this.getKey(route);
    if (this.storedRoutes.has(key)) {
      const storedRoute = this.storedRoutes.get(key)!;
      // Directly check if the current time is beyond the expiry time
      if (Date.now() > storedRoute.expiry) {
        // If expired, remove from storage and return false
        this.storedRoutes.delete(key);
        return false;
      }
      return true;
    }
    return false;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    let key = this.getKey(route);
    if (!route?.routeConfig || !this.storedRoutes.has(key)) {
      return null;
    }
    return this.storedRoutes.get(key).handle;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  getKey(route) {
    // Attempt to access the component name, if available
    const componentName = route.component ? route.component['name'] : 'unknown';

    // Extract all route params to form part of the key
    const paramsKey = Object.keys(route.params)
      .sort() // Ensure the order of parameters is consistent
      .map((key) => `${key}=${route.params[key]}`)
      .join('&');

    // Concatenate to form a unique key
    const key = `${componentName}?${paramsKey}`;
    // console.log("Caching key: " + key)
    return key;
  }
}
